import { KeywordAppearance } from 'types/supplierSearch';
import * as Yup from 'yup';

export const viewStateFormConstants = {
  keywordFilters: { label: 'Keyword', id: 'keywordFilters' as const },
  enterpriseClassFilters: {
    label: 'Supplier Class',
    id: 'enterpriseClassFilters' as const,
  },
  certificationFilters: {
    label: 'Certification',
    id: 'certificationFilters' as const,
  },
  locationFilters: { label: 'Location', id: 'locationFilters' as const },
};

const autocompleteOptionSchema = Yup.object({
  value: Yup.string().required(),
  longName: Yup.string().required(),
  shortName: Yup.string().required(),
  active: Yup.boolean().required(),
});
export const viewStateFormSchema = Yup.object({
  [viewStateFormConstants.keywordFilters.id]: Yup.array().of(
    Yup.object({
      query: Yup.string().required(),
      appearance: Yup.mixed<KeywordAppearance>()
        .oneOf(Object.values(KeywordAppearance))
        .required(),
      active: Yup.boolean().required(),
    })
  ),
  [viewStateFormConstants.enterpriseClassFilters.id]: Yup.array().of(
    autocompleteOptionSchema
  ),
  [viewStateFormConstants.certificationFilters.id]: Yup.array().of(
    autocompleteOptionSchema
  ),
  [viewStateFormConstants.locationFilters.id]: Yup.array().of(
    autocompleteOptionSchema
  ),
});

export const ENTERPRISE_CLASS_OPTIONS = {
  ontPropertyComponentId: 'enterpriseClass',
  values: [
    {
      value: '17eef68a-c52e-4794-aaf6-1ac05a9a38c9',
      shortName: 'Machine Shop',
      longName: 'Machine Shop',
    },
    {
      value: '3bc01042-bd77-4f46-8451-2e066b959d39',
      shortName: 'Metal Fabricator',
      longName: 'Metal Fabricator',
    },
    {
      value: '645d1777-5826-4484-8c06-0a922a64b27e',
      shortName: 'Mechanical Contract Manufacturer',
      longName: 'Mechanical Contract Manufacturer',
    },
    {
      value: '3928313c-fd50-42c9-8904-2a396d5a26b7',
      shortName: 'Sheet Metal Fabricator',
      longName: 'Sheet Metal Fabricator',
    },
    {
      value: 'd21be9b0-205c-434a-a05b-0c788c4cb14a',
      shortName: 'Electronics Contract Manufacturer',
      longName: 'Electronics Contract Manufacturer',
    },
    {
      value: '872de2d3-444c-4dab-baed-794f242d1126',
      shortName: 'Plastic Injection Molder',
      longName: 'Plastic Injection Molder',
    },
    {
      value: '9f0c44a6-e324-4786-9dc0-39ae5515ae68',
      shortName: 'Electronics Manufacturing Services',
      longName: 'Electronics Manufacturing Services',
    },
    {
      value: 'd73ca243-c3c5-48a3-82ce-58bfdcc3302e',
      shortName: 'Wire Harness Assembler',
      longName: 'Wire Harness Assembler',
    },
    {
      value: '3a453c16-98f2-405d-bddb-bcc7497c5b9a',
      shortName: 'Printed Circuit Board Assembler PCBA',
      longName: 'Printed Circuit Board Assembler PCBA',
    },
    {
      value: 'dde2b1d3-79a3-4afc-8f0b-926276ccfb0e',
      shortName: 'Electrical Contract Manufacturer',
      longName: 'Electrical Contract Manufacturer',
    },
    {
      value: '7c9cb4df-31ef-46ff-8aef-4d633930d377',
      shortName: 'Cable Manufacturer',
      longName: 'Cable Manufacturer',
    },
    {
      value: 'e6ccc1d1-dcfc-457f-a47c-d62652b54e5d',
      shortName: 'Sheet Metal Stamping',
      longName: 'Sheet Metal Stamping',
    },
    {
      value: '98b6ed86-8ffe-435b-a26e-a6b9533efe43',
      shortName: 'Printed Circuit Board Fabricator PCBF',
      longName: 'Printed Circuit Board Fabricator PCBF',
    },
  ]
    .map((option) => ({ ...option, active: true }))
    .sort((a, b) => a.shortName.localeCompare(b.shortName)),
};

export const CERTIFICATION_OPTIONS = {
  values: [
    {
      value: 'fb7c8285-f80f-40b2-ae4b-80920bf21ee5',
      longName: 'ISO',
      shortName: 'ISO',
    },
    {
      value: '2c7b9ea2-669c-413e-bfff-e3b1beaa038f',
      longName: 'ISO 9001',
      shortName: 'ISO 9001',
    },
    {
      value: 'f0afb1ca-1488-4e7d-9270-f3c068612ddd',
      longName: '9001:2015',
      shortName: '9001:2015',
    },
    {
      value: 'ad95f832-66bb-440f-a78a-7da7ee8db461',
      longName: 'AS',
      shortName: 'AS',
    },
    {
      value: '824d0f1b-73ba-45e9-aea5-01f59279e110',
      longName: '9100',
      shortName: '9100',
    },
    {
      value: '9e047e53-5cdc-4fd8-921b-90dc381baa10',
      longName: 'ITAR Registered',
      shortName: 'ITAR Registered',
    },
    {
      value: '05388e09-fd6c-4851-86ae-f4ce0565fa6b',
      longName: 'ASME',
      shortName: 'ASME',
    },
    {
      value: 'f003b861-e0a2-4ad3-a00a-f40d02ba7819',
      longName: '9100D',
      shortName: '9100D',
    },
    {
      value: 'c201d63c-034c-4af6-b44b-c01463f91533',
      longName: '14001',
      shortName: '14001',
    },
    {
      value: 'd07564c3-7b3a-4361-a94e-56c230c9346c',
      longName: '9001:2008',
      shortName: '9001:2008',
    },
    {
      value: '8a3abaad-cca7-4b7f-af92-bbc83c2bc038',
      longName: '13485',
      shortName: '13485',
    },
    {
      value: '32845e78-1901-44af-b35e-7638d700a05f',
      longName: '16949',
      shortName: '16949',
    },
    {
      value: '8797fddb-8d36-40ba-8d5f-b7a40de32bd2',
      longName: 'IATF',
      shortName: 'IATF',
    },
    {
      value: '84a2c7f2-39b1-427f-a232-39479a6dee48',
      longName: '17025',
      shortName: '17025',
    },
    {
      value: 'dfe31473-53f6-40fe-9876-e8e0262af6b6',
      longName: '9000',
      shortName: '9000',
    },
    {
      value: '5783047f-374d-4cad-846e-25ca072fdfbe',
      longName: '13485:2016',
      shortName: '13485:2016',
    },
    {
      value: 'a6258894-c35a-432b-a5bd-5e62cdb0e1cb',
      longName: '14001:2015',
      shortName: '14001:2015',
    },
    {
      value: 'df982aed-606b-404b-9547-c056bd828f32',
      longName: 'IPC Standard',
      shortName: 'IPC Standard',
    },
    {
      value: '29eb91ce-66f5-4bcd-bbe7-d2d28088a7ef',
      longName: '9001:2000',
      shortName: '9001:2000',
    },
    {
      value: '94efebd5-8d5d-4ad3-afe6-4e0912b274ae',
      longName: 'UL',
      shortName: 'UL',
    },
    {
      value: 'c1a495b2-9563-4614-aa1f-0d5f7a251a01',
      longName: 'IPC-A-610',
      shortName: 'IPC-A-610',
    },
    {
      value: '921dbeae-0586-4248-a88a-8299b26bf746',
      longName: 'UL 508A',
      shortName: 'UL 508A',
    },
    {
      value: 'bf5af6fb-d64a-471c-97aa-b5befec815c2',
      longName: '9002',
      shortName: '9002',
    },
    {
      value: 'ced9c30e-4eed-4999-bc57-ddd64f07afc5',
      longName: '9102',
      shortName: '9102',
    },
    {
      value: 'e61e048c-370c-4afd-ab96-2033aa9dcd87',
      longName: '9100C',
      shortName: '9100C',
    },
    {
      value: '2356ee53-2877-407c-96dd-102f5b5706c6',
      longName: '17025:2017',
      shortName: '17025:2017',
    },
    {
      value: 'c2197dc3-330f-49d4-9602-453e8e08a69b',
      longName: 'IPC/WHMA-A-620E',
      shortName: 'IPC/WHMA-A-620E',
    },
    {
      value: '974c22ae-4f1e-45be-a66c-ebb966884fbd',
      longName: 'ESD',
      shortName: 'ESD',
    },
    {
      value: 'c28a4a6c-0a77-458f-8472-d77e10c01ed0',
      longName: 'S20.20',
      shortName: 'S20.20',
    },
    {
      value: 'ade51560-234b-40cc-ac8b-048041607cc5',
      longName: '9120',
      shortName: '9120',
    },
    {
      value: '0a6a43df-5aca-4e2b-b777-2525dc8600aa',
      longName: '14000',
      shortName: '14000',
    },
    {
      value: '0a6bd8be-77a0-4081-a20b-24f298c82a13',
      longName: '13485:2003',
      shortName: '13485:2003',
    },
    {
      value: '64f74274-80b1-4983-9d58-63aac901b965',
      longName: '17025:2005',
      shortName: '17025:2005',
    },
    {
      value: 'b8f6c571-83c8-468a-9bac-2740de4de0a3',
      longName: '9000:2015',
      shortName: '9000:2015',
    },
    {
      value: '067e571c-0dc0-4af5-abaf-88df633c3b49',
      longName: '9110',
      shortName: '9110',
    },
    {
      value: '399b37fe-a030-4d89-abbd-cefbfc0712b1',
      longName: '9000',
      shortName: '9000',
    },
    {
      value: 'b0a13081-6d7e-4490-ac09-8eb07d7194f3',
      longName: '9003',
      shortName: '9003',
    },
    {
      value: '86175ddd-d0c7-46c9-b26a-7f202af4c02b',
      longName: '9000:2008',
      shortName: '9000:2008',
    },
    {
      value: '2981f975-add5-498b-9013-c834900f523b',
      longName: '9000:2000',
      shortName: '9000:2000',
    },
    {
      value: 'fd42a3c5-5b84-4526-acad-4c60122a4b60',
      longName: 'ASME Nuclear',
      shortName: 'ASME Nuclear',
    },
    {
      value: '846449bc-3e64-414c-8480-8caa8b0ab5f5',
      longName: '9004',
      shortName: '9004',
    },
    {
      value: '154b6428-2f5e-4756-bdc5-fc8630ddb01e',
      longName: '14001:2009',
      shortName: '14001:2009',
    },
    {
      value: '24cff165-8f03-487c-a91b-080a5d03dd1d',
      longName: '9003',
      shortName: '9003',
    },
    {
      value: '7a451d2d-e39d-465a-8eb4-14004cc4a3b1',
      longName: 'CMMC',
      shortName: 'CMMC',
    },
    {
      value: '8abc452e-379f-4e18-91bc-638d9c1c697d',
      longName: 'IPC-A-630',
      shortName: 'IPC-A-630',
    },
    {
      value: 'bce89fd2-d2d9-4fc9-91d8-5d0d2aae1b04',
      longName: 'NADCAP',
      shortName: 'NADCAP',
    },
    {
      value: '83096e71-8418-4cc8-a1de-1ce4493cb08a',
      longName: 'IPC Class 3',
      shortName: 'IPC Class 3',
    },
    {
      value: '8d736c6f-c566-4e55-8b36-e3b4de177643',
      longName: 'IPC Class',
      shortName: 'IPC Class',
    },
    {
      value: 'cf6dab2f-4265-4bff-ab8f-a8ae207fa766',
      longName: 'TL',
      shortName: 'TL',
    },
    {
      value: 'db006407-8bd9-4849-937c-d59e23e59e4b',
      longName: '9000',
      shortName: '9000',
    },
    {
      value: '58180740-e7fa-4674-ac89-c094021f12aa',
      longName: 'ATEX',
      shortName: 'ATEX',
    },
    {
      value: '3aa6271b-e1a1-42c1-960e-992ec5c802b5',
      longName: '7003',
      shortName: '7003',
    },
    {
      value: '5e7a7345-5207-4263-aeaa-63c2c88fd328',
      longName: 'IECEx',
      shortName: 'IECEx',
    },
    {
      value: 'aac2d024-41a1-47bf-984b-0d3bc9e2283c',
      longName: 'ETL',
      shortName: 'ETL',
    },
  ]
    .map((option) => ({ ...option, active: true }))
    .sort((a, b) => a.shortName.localeCompare(b.shortName)),
  ontPropertyComponentId: 'dffbeb97-9d75-436d-a6de-3f10533f4921',
};

export const ENTERPRISE_LOCATION_OPTIONS = {
  values: [
    {
      value: 'California',
      longName: 'California',
      shortName: 'California',
    },
    {
      value: 'Texas',
      longName: 'Texas',
      shortName: 'Texas',
    },
    {
      value: 'Florida',
      longName: 'Florida',
      shortName: 'Florida',
    },
    {
      value: 'New York',
      longName: 'New York',
      shortName: 'New York',
    },
    {
      value: 'Illinois',
      longName: 'Illinois',
      shortName: 'Illinois',
    },
    {
      value: 'Ohio',
      longName: 'Ohio',
      shortName: 'Ohio',
    },
    {
      value: 'Pennsylvania',
      longName: 'Pennsylvania',
      shortName: 'Pennsylvania',
    },
    {
      value: 'Michigan',
      longName: 'Michigan',
      shortName: 'Michigan',
    },
    {
      value: 'New Jersey',
      longName: 'New Jersey',
      shortName: 'New Jersey',
    },
    {
      value: 'Georgia',
      longName: 'Georgia',
      shortName: 'Georgia',
    },
    {
      value: 'Massachusetts',
      longName: 'Massachusetts',
      shortName: 'Massachusetts',
    },
    {
      value: 'Wisconsin',
      longName: 'Wisconsin',
      shortName: 'Wisconsin',
    },
    {
      value: 'Indiana',
      longName: 'Indiana',
      shortName: 'Indiana',
    },
    {
      value: 'North Carolina',
      longName: 'North Carolina',
      shortName: 'North Carolina',
    },
    {
      value: 'Washington',
      longName: 'Washington',
      shortName: 'Washington',
    },
    {
      value: 'Colorado',
      longName: 'Colorado',
      shortName: 'Colorado',
    },
    {
      value: 'Tennessee',
      longName: 'Tennessee',
      shortName: 'Tennessee',
    },
    {
      value: 'Missouri',
      longName: 'Missouri',
      shortName: 'Missouri',
    },
    {
      value: 'Virginia',
      longName: 'Virginia',
      shortName: 'Virginia',
    },
    {
      value: 'Arizona',
      longName: 'Arizona',
      shortName: 'Arizona',
    },
    {
      value: 'Louisiana',
      longName: 'Louisiana',
      shortName: 'Louisiana',
    },
    {
      value: 'Alabama',
      longName: 'Alabama',
      shortName: 'Alabama',
    },
    {
      value: 'Maryland',
      longName: 'Maryland',
      shortName: 'Maryland',
    },
    {
      value: 'South Carolina',
      longName: 'South Carolina',
      shortName: 'South Carolina',
    },
    {
      value: 'Oregon',
      longName: 'Oregon',
      shortName: 'Oregon',
    },
    {
      value: 'Oklahoma',
      longName: 'Oklahoma',
      shortName: 'Oklahoma',
    },
    {
      value: 'Connecticut',
      longName: 'Connecticut',
      shortName: 'Connecticut',
    },
    {
      value: 'Utah',
      longName: 'Utah',
      shortName: 'Utah',
    },
    {
      value: 'Kentucky',
      longName: 'Kentucky',
      shortName: 'Kentucky',
    },
    {
      value: 'Iowa',
      longName: 'Iowa',
      shortName: 'Iowa',
    },
    {
      value: 'Kansas',
      longName: 'Kansas',
      shortName: 'Kansas',
    },
    {
      value: 'New Hampshire',
      longName: 'New Hampshire',
      shortName: 'New Hampshire',
    },
    {
      value: 'Mississippi',
      longName: 'Mississippi',
      shortName: 'Mississippi',
    },
    {
      value: 'Arkansas',
      longName: 'Arkansas',
      shortName: 'Arkansas',
    },
    {
      value: 'Nevada',
      longName: 'Nevada',
      shortName: 'Nevada',
    },
    {
      value: 'Ontario',
      longName: 'Ontario',
      shortName: 'Ontario',
    },
    {
      value: 'Nebraska',
      longName: 'Nebraska',
      shortName: 'Nebraska',
    },
    {
      value: 'Idaho',
      longName: 'Idaho',
      shortName: 'Idaho',
    },
    {
      value: 'Rhode Island',
      longName: 'Rhode Island',
      shortName: 'Rhode Island',
    },
    {
      value: 'Nuevo León',
      longName: 'Nuevo León',
      shortName: 'Nuevo León',
    },
    {
      value: 'Maine',
      longName: 'Maine',
      shortName: 'Maine',
    },
    {
      value: 'New Mexico',
      longName: 'New Mexico',
      shortName: 'New Mexico',
    },
    {
      value: 'West Virginia',
      longName: 'West Virginia',
      shortName: 'West Virginia',
    },
    {
      value: 'Montana',
      longName: 'Montana',
      shortName: 'Montana',
    },
    {
      value: 'South Dakota',
      longName: 'South Dakota',
      shortName: 'South Dakota',
    },
    {
      value: 'Puerto Rico',
      longName: 'Puerto Rico',
      shortName: 'Puerto Rico',
    },
    {
      value: 'Hawaii',
      longName: 'Hawaii',
      shortName: 'Hawaii',
    },
    {
      value: 'Vermont',
      longName: 'Vermont',
      shortName: 'Vermont',
    },
    {
      value: 'Delaware',
      longName: 'Delaware',
      shortName: 'Delaware',
    },
    {
      value: 'North Dakota',
      longName: 'North Dakota',
      shortName: 'North Dakota',
    },
    {
      value: 'Wyoming',
      longName: 'Wyoming',
      shortName: 'Wyoming',
    },
    {
      value: 'Guanajuato',
      longName: 'Guanajuato',
      shortName: 'Guanajuato',
    },
    {
      value: 'British Columbia',
      longName: 'British Columbia',
      shortName: 'British Columbia',
    },
    {
      value: 'Querétaro',
      longName: 'Querétaro',
      shortName: 'Querétaro',
    },
    {
      value: 'Alberta',
      longName: 'Alberta',
      shortName: 'Alberta',
    },
    {
      value: 'Alaska',
      longName: 'Alaska',
      shortName: 'Alaska',
    },
    {
      value: 'Puebla',
      longName: 'Puebla',
      shortName: 'Puebla',
    },
    {
      value: 'Baja California',
      longName: 'Baja California',
      shortName: 'Baja California',
    },
    {
      value: 'Chihuahua',
      longName: 'Chihuahua',
      shortName: 'Chihuahua',
    },
    {
      value: 'Coahuila',
      longName: 'Coahuila',
      shortName: 'Coahuila',
    },
    {
      value: 'Ciudad De Mexico',
      longName: 'Ciudad De Mexico',
      shortName: 'Ciudad De Mexico',
    },
    {
      value: 'Aguascalientes',
      longName: 'Aguascalientes',
      shortName: 'Aguascalientes',
    },
    {
      value: 'Manitoba',
      longName: 'Manitoba',
      shortName: 'Manitoba',
    },
    {
      value: 'Nova Scotia',
      longName: 'Nova Scotia',
      shortName: 'Nova Scotia',
    },
    {
      value: 'Saskatchewan',
      longName: 'Saskatchewan',
      shortName: 'Saskatchewan',
    },
    {
      value: 'New Brunswick',
      longName: 'New Brunswick',
      shortName: 'New Brunswick',
    },
    {
      value: 'State of Mexico',
      longName: 'State of Mexico',
      shortName: 'State of Mexico',
    },
    {
      value: 'Guam',
      longName: 'Guam',
      shortName: 'Guam',
    },
    {
      value: 'Jalisco',
      longName: 'Jalisco',
      shortName: 'Jalisco',
    },
    {
      value: 'United States Virgin Islands',
      longName: 'United States Virgin Islands',
      shortName: 'United States Virgin Islands',
    },
    {
      value: 'Zacatecas',
      longName: 'Zacatecas',
      shortName: 'Zacatecas',
    },
    {
      value: 'Tamaulipas',
      longName: 'Tamaulipas',
      shortName: 'Tamaulipas',
    },
    {
      value: 'Newfoundland and Labrador',
      longName: 'Newfoundland and Labrador',
      shortName: 'Newfoundland and Labrador',
    },
    {
      value: 'Northern Mariana Islands',
      longName: 'Northern Mariana Islands',
      shortName: 'Northern Mariana Islands',
    },
    {
      value: 'Prince Edward Island',
      longName: 'Prince Edward Island',
      shortName: 'Prince Edward Island',
    },
    {
      value: 'Sonora',
      longName: 'Sonora',
      shortName: 'Sonora',
    },
    {
      value: 'Québec',
      longName: 'Québec',
      shortName: 'Québec',
    },
    {
      value: 'Veracruz',
      longName: 'Veracruz',
      shortName: 'Veracruz',
    },
    {
      value: 'Hidalgo',
      longName: 'Hidalgo',
      shortName: 'Hidalgo',
    },
    {
      value: 'Northwest Territories',
      longName: 'Northwest Territories',
      shortName: 'Northwest Territories',
    },
    {
      value: 'Tlaxcala',
      longName: 'Tlaxcala',
      shortName: 'Tlaxcala',
    },
    {
      value: 'Michoacán',
      longName: 'Michoacán',
      shortName: 'Michoacán',
    },
    {
      value: 'American Samoa',
      longName: 'American Samoa',
      shortName: 'American Samoa',
    },
    {
      value: 'Baja California Sur',
      longName: 'Baja California Sur',
      shortName: 'Baja California Sur',
    },
    {
      value: 'Durango',
      longName: 'Durango',
      shortName: 'Durango',
    },
    {
      value: 'Sinaloa',
      longName: 'Sinaloa',
      shortName: 'Sinaloa',
    },
    {
      value: 'Yucatán',
      longName: 'Yucatán',
      shortName: 'Yucatán',
    },
    {
      value: 'Yukon',
      longName: 'Yukon',
      shortName: 'Yukon',
    },
    {
      value: 'Campeche',
      longName: 'Campeche',
      shortName: 'Campeche',
    },
    {
      value: 'San Luis Potosí',
      longName: 'San Luis Potosí',
      shortName: 'San Luis Potosí',
    },
    {
      value: 'Quintana Roo',
      longName: 'Quintana Roo',
      shortName: 'Quintana Roo',
    },
    {
      value: 'Bavaria',
      longName: 'Bavaria',
      shortName: 'Bavaria',
    },
    {
      value: 'Chiapas',
      longName: 'Chiapas',
      shortName: 'Chiapas',
    },
    {
      value: 'District of columbia',
      longName: 'District of columbia',
      shortName: 'District of columbia',
    },
    {
      value: 'Estado de México',
      longName: 'Estado de México',
      shortName: 'Estado de México',
    },
    {
      value: 'Nayarit',
      longName: 'Nayarit',
      shortName: 'Nayarit',
    },
    {
      value: 'England',
      longName: 'England',
      shortName: 'England',
    },
    {
      value: 'Guerrero',
      longName: 'Guerrero',
      shortName: 'Guerrero',
    },
    {
      value: 'Minnesota',
      longName: 'Minnesota',
      shortName: 'Minnesota',
    },
    {
      value: 'Morelos',
      longName: 'Morelos',
      shortName: 'Morelos',
    },
    {
      value: 'Nunavut',
      longName: 'Nunavut',
      shortName: 'Nunavut',
    },
    {
      value: 'Oaxaca',
      longName: 'Oaxaca',
      shortName: 'Oaxaca',
    },
    {
      value: 'Rio Grande do Sul',
      longName: 'Rio Grande do Sul',
      shortName: 'Rio Grande do Sul',
    },
    {
      value: 'Tabasco',
      longName: 'Tabasco',
      shortName: 'Tabasco',
    },
    {
      value: 'Tamil Nandu',
      longName: 'Tamil Nandu',
      shortName: 'Tamil Nandu',
    },
    {
      value: 'Yukon Territory',
      longName: 'Yukon Territory',
      shortName: 'Yukon Territory',
    },
  ]
    .map((option) => ({ ...option, active: true }))
    .sort((a, b) => a.shortName.localeCompare(b.shortName)),
  ontPropertyComponentId: 'ce3339c6-0e11-4c57-96d5-31940808dd2f',
};

export const STATE_VARIATIONS: { [state: string]: string[] } = {
  Alabama: ['AL', 'Alabama'],
  Alaska: ['AK', 'Alaska'],
  Arizona: ['AZ', 'Arizona'],
  Arkansas: ['AR', 'Arkansas'],
  California: ['CA', 'California'],
  Colorado: ['CO', 'Colorado'],
  Connecticut: ['CT', 'Connecticut'],
  Delaware: ['DE', 'Delaware'],
  'District of Columbia': ['DC', 'District of Columbia'],
  Florida: ['FL', 'Florida'],
  Georgia: ['GA', 'Georgia'],
  Hawaii: ['HI', 'Hawaii'],
  Idaho: ['ID', 'Idaho'],
  Illinois: ['IL', 'Illinois'],
  Indiana: ['IN', 'Indiana'],
  Iowa: ['IA', 'Iowa'],
  Kansas: ['KS', 'Kansas'],
  Kentucky: ['KY', 'Kentucky'],
  Louisiana: ['LA', 'Louisiana'],
  Maine: ['ME', 'Maine'],
  Maryland: ['MD', 'Maryland'],
  Massachusetts: ['MA', 'Massachusetts'],
  Michigan: ['MI', 'Michigan'],
  Minnesota: ['MN', 'Minnesota'],
  Mississippi: ['MS', 'Mississippi'],
  Missouri: ['MO', 'Missouri'],
  Montana: ['MT', 'Montana'],
  Nebraska: ['NE', 'Nebraska'],
  Nevada: ['NV', 'Nevada'],
  'New Hampshire': ['NH', 'New Hampshire'],
  'New Jersey': ['NJ', 'New Jersey'],
  'New Mexico': ['NM', 'New Mexico'],
  'New York': ['NY', 'New York'],
  'North Carolina': ['NC', 'North Carolina'],
  'North Dakota': ['ND', 'North Dakota'],
  Ohio: ['OH', 'Ohio'],
  Oklahoma: ['OK', 'Oklahoma'],
  Oregon: ['OR', 'Oregon'],
  Pennsylvania: ['PA', 'Pennsylvania'],
  'Rhode Island': ['RI', 'Rhode Island'],
  'South Carolina': ['SC', 'South Carolina'],
  'South Dakota': ['SD', 'South Dakota'],
  Tennessee: ['TN', 'Tennessee'],
  Texas: ['TX', 'Texas'],
  Utah: ['UT', 'Utah'],
  Vermont: ['VT', 'Vermont'],
  Virginia: ['VA', 'Virginia'],
  Washington: ['WA', 'Washington'],
  'West Virginia': ['WV', 'West Virginia'],
  Wisconsin: ['WI', 'Wisconsin'],
  Wyoming: ['WY', 'Wyoming'],
  'American Samoa': ['AS', 'American Samoa'],
  Guam: ['GU', 'Guam'],
  'Northern Mariana Islands': ['MP', 'Northern Mariana Islands'],
  'Puerto Rico': ['PR', 'Puerto Rico'],
  'U.S. Virgin Islands': ['VI', 'U.S. Virgin Islands'],
};

export const STATE_EQUIVALENCE_MAP: { [state: string]: string } = (() => {
  const map: { [state: string]: string } = {};
  for (const state of Object.keys(STATE_VARIATIONS)) {
    for (const stateCode of STATE_VARIATIONS[state]) {
      map[stateCode] = state;
    }
  }
  return map;
})();

export const SUMMARY_CARD_HEIGHT = 525;
export const SUMMARY_CARD_WIDTH = 350;
export const SUMMARY_CARD_BORDER_RADIUS = 8;

export const supplierSummaryCardStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  justifyContent: 'flex-start',
  width: `${SUMMARY_CARD_WIDTH}px`,
  height: `${SUMMARY_CARD_HEIGHT}px`,
  bgcolor: 'background.paper',
  borderRadius: `${SUMMARY_CARD_BORDER_RADIUS}px`,
  borderWidth: 1,
  borderStyle: 'solid',
  borderTopColor: 'grey.300',
  borderLeftColor: 'grey.300',
  borderBottomColor: 'grey.300',
  borderRightColor: 'grey.300',
} as const;
